import React, { FC, ReactNode } from 'react'
import cn from 'classnames'
import Tooltip from 'components/base/Tooltip'
import Input, { InputType } from './Input'
import { HelpOutlineIcon } from 'components/icons'

export interface InputGroupProps extends Omit<InputType, 'title'> {
  action?: ReactNode
  component?: ReactNode
  error?: string
  errorMessage?: string
  inputProps?: InputType
  required?: boolean
  title?: ReactNode
  tooltip?: string
}

const InputGroup: FC<InputGroupProps> = ({
  action,
  className,
  component,
  defaultValue,
  disabled,
  error,
  errorMessage,
  id: providedId,
  inputProps = {},
  inputRef,
  isValid,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  textarea,
  title,
  tooltip,
  touched,
  type = 'text',
  value,
}) => {
  const id = providedId || name || inputProps.id || inputProps.name

  const computedIsValid =
    isValid !== undefined ? isValid : touched && error ? false : undefined

  const computedErrorMessage =
    errorMessage !== undefined
      ? errorMessage
      : touched && error
        ? error
        : undefined

  return (
    <div className={cn('form-group', className)}>
      {(title || action) && (
        <div className='flex-row'>
          <Tooltip place='top' content={tooltip}>
            <label
              className='form-label d-flex align-items-center fw-semibold flex-fill'
              htmlFor={id}
            >
              {title}
              {tooltip && (
                <HelpOutlineIcon width={18} className='mx-2 text-dark300' />
              )}
              {required && <span className='text-danger'>*</span>}
            </label>
            {action}
          </Tooltip>
        </div>
      )}

      {component ?? (
        <Input
          name={name}
          touched={touched}
          textarea={textarea}
          inputRef={inputRef}
          onBlur={onBlur}
          {...inputProps}
          isValid={computedIsValid}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          type={type}
          id={id}
          placeholder={placeholder}
        />
      )}

      {computedErrorMessage && (
        <span
          id={inputProps.name ? `${inputProps.name}-error` : undefined}
          className='text-danger'
        >
          {computedErrorMessage}
        </span>
      )}
    </div>
  )
}

InputGroup.displayName = 'InputGroup'
export default InputGroup
